import { useEffect, useState } from "react";
import { getMutualFundData } from "../../Services/MutualFundServices";
import useVendorStore from "../../AuthStore/store/authStore";
import { FiSearch, FiFilter, FiArrowUp, FiArrowDown, FiX, FiRefreshCw, FiAlertCircle, FiTrendingUp, FiInfo, FiGrid, FiTag } from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";

const MutualFunds = () => {
  const [funds, setFunds] = useState([]);
  const [filteredFunds, setFilteredFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("annual_return");
  const [activeMobilePanel, setActiveMobilePanel] = useState(null);
  const navigate = useNavigate();
  const { authToken } = useVendorStore();

  useEffect(() => {
    const fetchFunds = async () => {
      try {
        setLoading(true);
        setError("");

        if (!authToken) {
          setError("No auth token found. Please log in first.");
          setLoading(false);
          return;
        }

        const response = await getMutualFundData(authToken);
        setFunds(response || []);
        setFilteredFunds(response || []);
      } catch (err) {
        setError("Failed to fetch mutual funds. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchFunds();
  }, [authToken]);

  // Get unique fund types and categories for filters
  const fundTypes = [...new Set(funds.map(fund => fund.type))];
  const fundCategories = [...new Set(funds.map(fund => fund.category))];

  // Filter and sort funds
  useEffect(() => {
    let result = [...funds];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(fund =>
        fund.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fund.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fund.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== "all") {
      result = result.filter(fund => fund.type === filterType);
    }

    // Apply category filter
    if (filterCategory !== "all") {
      result = result.filter(fund => fund.category === filterCategory);
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === "annual_return") {
        return b.annual_return - a.annual_return;
      } else if (sortBy === "min_amount") {
        return a.min_amount - b.min_amount;
      } else if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

    setFilteredFunds(result);
  }, [funds, searchTerm, filterType, filterCategory, sortBy]);

  // Toggle mobile panels
  const toggleMobilePanel = (panel) => {
    if (activeMobilePanel === panel) {
      setActiveMobilePanel(null);
    } else {
      setActiveMobilePanel(panel);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterCategory("all");
    setSortBy("annual_return");
  };

  const handleInvestNow = (fund) => {
    navigate("/policy-buy", {
      state: {
        fundName: fund.name,
        fundType: fund.type,
      }
    });
  };

  const handleScroll = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto text-center px-6 relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Mutual Funds</h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8">
            Explore our curated list of mutual funds tailored to your financial
            goals and risk profile.
          </p>
          <button className="bg-white text-blue-700 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-all transform hover:-translate-y-1 shadow-lg">
            Start Investing
          </button>
        </div>
      </section>

      {/* Mobile Filter Controls */}
      <div className="lg:hidden sticky top-0 z-20 bg-white shadow-sm py-3 px-4 ">
        <div className="flex justify-between items-center">
          <div className="flex space-x-2 ">
            {/* Search Button */}
            <button
              onClick={() => toggleMobilePanel('search')}
              className={`p-3 rounded-lg flex items-center ${activeMobilePanel === 'search' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
            >
              <FiSearch className="w-5 h-5" />
            </button>

            {/* Filter Button */}
            <button
              onClick={() => toggleMobilePanel('filter')}
              className={`p-3 rounded-lg flex items-center ${activeMobilePanel === 'filter' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
            >
              <FiFilter className="w-5 h-5" />
            </button>

            {/* Sort Button */}
            <button
              onClick={() => toggleMobilePanel('sort')}
              className={`p-3 rounded-lg flex items-center ${activeMobilePanel === 'sort' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
            >
              {sortBy === 'annual_return' ? <FiTrendingUp className="w-5 h-5" /> :
                sortBy === 'min_amount' ? <FiArrowDown className="w-5 h-5" /> :
                  <FiArrowUp className="w-5 h-5 " />}
            </button>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            {filteredFunds.length} of {funds.length}
          </div>
        </div>

        {/* Mobile Search Panel */}
        {activeMobilePanel === 'search' && (
          <div className="mt-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search funds..."
                className="w-full px-4 py-2 pl-10 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FiSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Mobile Filter Panel */}
        {activeMobilePanel === 'filter' && (
          <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <FiGrid className="mr-2" />
                Fund Type
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-2 rounded-lg text-sm ${filterType === 'all' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  All Types
                </button>
                {fundTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type)}
                    className={`px-3 py-2 rounded-lg text-sm ${filterType === type ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700'} capitalize`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <FiTag className="mr-2" />
                Category
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setFilterCategory('all')}
                  className={`px-3 py-2 rounded-lg text-sm ${filterCategory === 'all' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  All Categories
                </button>
                {fundCategories.map(category => (
                  <button
                    key={category}
                    onClick={() => setFilterCategory(category)}
                    className={`px-3 py-2 rounded-lg text-sm ${filterCategory === category ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700'} capitalize`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={resetFilters}
              className="w-full mt-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Mobile Sort Panel */}
        {activeMobilePanel === 'sort' && (
          <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Sort By</h4>
            <div className="space-y-2">
              <button
                onClick={() => setSortBy('annual_return')}
                className={`flex items-center w-full px-3 py-2 rounded-lg text-sm ${sortBy === 'annual_return' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
              >
                <FiTrendingUp className="mr-2" />
                Highest Return
              </button>
              <button
                onClick={() => setSortBy('min_amount')}
                className={`flex items-center w-full px-3 py-2 rounded-lg text-sm ${sortBy === 'min_amount' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
              >
                <FiArrowDown className="mr-2" />
                Lowest Investment
              </button>
              <button
                onClick={() => setSortBy('name')}
                className={`flex items-center w-full px-3 py-2 rounded-lg text-sm ${sortBy === 'name' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
              >
                <FiArrowUp className="mr-2" />
                Name (A-Z)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters Sidebar - Hidden on mobile */}
          <div className="hidden lg:block lg:w-1/4">
            <div className="bg-white rounded-2xl p-6 shadow-md sticky top-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                <button
                  onClick={resetFilters}
                  className="text-sm text-green-600 hover:text-green-700"
                >
                  Reset All
                </button>
              </div>

              {/* Search */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Search</h4>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search funds..."
                    className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <FiSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Fund Type */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                  <FiGrid className="mr-2" />
                  Fund Type
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="filterType"
                      value="all"
                      checked={filterType === "all"}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">All Types</span>
                  </label>
                  {fundTypes.map(type => (
                    <label key={type} className="flex items-center">
                      <input
                        type="radio"
                        name="filterType"
                        value={type}
                        checked={filterType === type}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="text-green-600 focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                  <FiTag className="mr-2" />
                  Category
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="filterCategory"
                      value="all"
                      checked={filterCategory === "all"}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">All Categories</span>
                  </label>
                  {fundCategories.map(category => (
                    <label key={category} className="flex items-center">
                      <input
                        type="radio"
                        name="filterCategory"
                        value={category}
                        checked={filterCategory === category}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="text-green-600 focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 capitalize">{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sort By */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Sort By</h4>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="annual_return">Highest Return</option>
                  <option value="min_amount">Lowest Investment</option>
                  <option value="name">Name (A-Z)</option>
                </select>
              </div>

              <button className="w-full bg-green-500 text-white py-2.5 rounded-lg font-medium hover:bg-green-600 transition-colors">
                Apply Filters
              </button>
            </div>
          </div>

          {/* Funds Grid Section */}
          <div className="w-full lg:w-3/4">
            {/* Desktop Results Counter */}
            <div className="hidden lg:flex bg-white rounded-xl p-4 shadow-sm mb-6 justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold text-green-600">{filteredFunds.length}</span> of <span className="font-semibold">{funds.length}</span> funds
                {(filterType !== 'all' || filterCategory !== 'all') && (
                  <span className="ml-3">
                    <span className="text-gray-400">•</span>
                    {filterType !== 'all' && (
                      <span className="ml-2 bg-green-100 text-green-700 px-2 py-1 rounded text-xs capitalize">
                        Type: {filterType}
                      </span>
                    )}
                    {filterCategory !== 'all' && (
                      <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs capitalize">
                        Category: {filterCategory}
                      </span>
                    )}
                  </span>
                )}
              </div>

              <button
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
              >
                <FiX className="mr-1" />
                Clear filters
              </button>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col justify-center items-center py-20">
                <div className="relative mb-6">
                  <div className="w-16 h-16 border-4 border-green-100 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-16 h-16 border-4 border-green-500 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <p className="text-gray-600 font-medium">Loading investment opportunities...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-white p-6 rounded-2xl shadow-md mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <FiAlertCircle className="h-6 w-6 text-red-500" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Connection Issue</h3>
                    <p className="text-red-700 mb-4">{error}</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center"
                    >
                      <FiRefreshCw className="mr-2" />
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredFunds.length === 0 && (
              <div className="bg-white p-8 rounded-2xl shadow-md text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <FiInfo className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No matching funds found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your filters or search terms</p>
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                >
                  Reset Filters
                </button>
              </div>
            )}

            {/* Funds Grid */}
            <div className="flex flex-col gap-6">
              {filteredFunds.map((fund) => (
                <div
                  key={fund.id}
                  className="bg-white rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 font-sans"
                >


                  {/* Fund Content */}
                  <div className="px-6 py-5">
                    <div className="flex justify-between items-center mb-4">
                      {/* Fund Name */}
                      <h2 className="text-lg font-bold text-gray-900">
                        {fund.name}
                      </h2>

                      {/* Trending Badge */}
                      {fund.trending ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-600">
                          <FiTrendingUp className="mr-1" />
                          Trending
                        </span>
                      ) : (
                        null
                      )}

                    </div>


                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium capitalize">
                        {fund.type}
                      </span>
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium capitalize">
                        {fund.category}
                      </span>
                      <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-medium">
                        {fund.fund_age} Years Old Fund
                      </span>
                    </div>

                    {/* Stats Section */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {/* Unique Investors */}
                      <div>
                        {/* Label */}
                        <p className="text-sm text-gray-500">Min Amount</p>

                        {/* Icon + Amount */}
                        <div className="flex items-center mt-1">
                          <FaRupeeSign className="text-gray-900 mr-1" />
                          <p className="font-bold text-gray-900">
                            {fund.min_amount?.toLocaleString() || "N/A"}
                          </p>
                        </div>
                      </div>

                      {/* Fund Size */}
                      <div>
                        <p className="text-sm text-gray-500">Min Tenure</p>
                        <p className="font-bold text-gray-900">
                          {fund.min_tenure
                            ? `${fund.min_tenure} yrs`
                            : "N/A"}
                        </p>
                      </div>


                      {/* Annualized Returns */}
                      <div>
                        <p className="text-sm text-gray-500">Annualized Returns</p>
                        <p
                          className={`font-bold ${fund.annual_return >= 10 ? "text-green-600" : "text-red-600"
                            }`}
                        >
                          {fund.annual_return.toFixed(2)}%
                        </p>
                        <p className="text-xs text-gray-500">Since Inception</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button className="flex-1  border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg font-medium transition">
                        Know More
                      </button>
                      <button
                        className="flex-1 bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg font-medium transition"
                        onClick={() => {
                          handleInvestNow(fund);
                          handleScroll();
                        }}
                      >
                        Invest Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>


            {/* View More Button */}
            {filteredFunds.length > 4 && (
              <div className="text-center mt-10">
                <button className="px-6 py-3 bg-white text-green-600 border border-green-300 rounded-xl font-medium hover:bg-green-50 transition-colors shadow-sm flex items-center justify-center mx-auto">
                  Load More Funds
                  <FiRefreshCw className="ml-2" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto text-center px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to start investing?</h2>
          <p className="text-xl max-w-2xl mx-auto mb-8">
            Join thousands of investors who trust us with their financial goals.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to='/sign-up' onClick={handleScroll}>
              <button className="bg-white text-blue-700 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition shadow-lg">
                Open an Account
              </button></Link>
            <Link to='/contact' onClick={handleScroll}>
              <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-700 transition">
                Contact Advisor
              </button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MutualFunds;